/**
 * API DIFFERENCES vs ../switch.tsx (shadcn):
 * Same API, includes icons.
 *
 * SIZING: use scale prop (default 4 → 52×32px with 24px thumb).
 */
'use client'

import { Switch as SwitchPrimitive } from '@base-ui/react/switch'
import type * as React from 'react'
import { cn } from '../lib/utils'
import { CheckIcon, XIcon } from './icon'

// oxlint-disable-next-line no-warning-comments -- TODO preserved per repo policy
// TODO: think about better transition.
/* SUS: the thumb probably is not perfectly vertically centered.
  In it's story it looks good, but inside a menu it's not (prominent when zoom in).
  Consider not using fractional number for the `scale`.
  Or the problem is with positioning with -translate-y-1/2
*/

const switchSizeToScale: Record<string, number> = { sm: 3, default: 4, lg: 5 }

function Switch({
  scale,
  size = 'default',
  className,
  style,
  ...props
}: SwitchPrimitive.Root.Props & {
  scale?: number
  size?: 'sm' | 'default' | 'lg'
}) {
  const effectiveScale = scale ?? switchSizeToScale[size]
  const width = 13 * effectiveScale
  const height = 8 * effectiveScale
  const thumb = 6 * effectiveScale

  const baseStyle: React.CSSProperties & Record<`--${string}`, string> = {
    '--w': `${width}px`,
    '--h': `${height}px`,
    '--thumb': `${thumb}px`,
    '--offset': `${effectiveScale}px`,
  }
  const mergedStyle = (
    state: SwitchPrimitive.Root.State,
  ): React.CSSProperties & Record<`--${string}`, string> => {
    const resolvedStyle =
      typeof style === 'function' ? style(state) : (style ?? undefined)

    return Object.assign({}, baseStyle, resolvedStyle)
  }

  return (
    <SwitchPrimitive.Root
      style={mergedStyle}
      className={cn(
        'border-border focus-visible:ring-primary/80 focus-visible:ring-offset-surface-1 data-checked:bg-primary data-checked:border-primary aria-invalid:border-error data-checked:aria-invalid:bg-error data-checked:aria-invalid:border-error relative inline-block h-(--h) w-(--w) shrink-0 cursor-pointer rounded-full border transition-colors duration-200 outline-none focus-visible:ring-4 focus-visible:ring-offset-4 data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      data-slot="switch"
      {...props}
    >
      <SwitchPrimitive.Thumb
        className="bg-border data-checked:bg-surface-1 not-data-checked:in-aria-[invalid=true]:bg-error pointer-events-none absolute top-1/2 left-(--offset) flex size-(--thumb) translate-x-0 -translate-y-1/2 items-center justify-center rounded-full transition-[transform,scale,background-color,color] duration-200 ease-in-out data-checked:translate-x-[calc(var(--w)-var(--offset)*2-var(--thumb))]"
        data-slot="switch-thumb"
      >
        <CheckIcon
          className="in-data-[checked]:text-primary absolute size-[60%] opacity-0 blur-sm transition-[opacity,filter] duration-200 in-data-[checked]:opacity-100 in-data-[checked]:blur-none"
          aria-hidden
        />
        <XIcon
          className="absolute size-[60%] blur-none transition-[opacity,filter] duration-200 in-data-[checked]:opacity-0 in-data-[checked]:blur-sm"
          aria-hidden
        />
      </SwitchPrimitive.Thumb>
    </SwitchPrimitive.Root>
  )
}

export { Switch }
